import React from 'react';
import { Linking } from 'react-native';
import {
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  ButtonText,
  ButtonIcon,
} from '@gluestack-ui/themed';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Video,
  Play,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

interface ExamPreflightChecklistProps {
  meetingUrl: string | null;
  isBookingAdmitted: boolean;
  isExamOpen: boolean;
  isExamClosed: boolean;
  onStartQuiz?: () => void;
}

interface GateRowProps {
  testID?: string;
  done: boolean;
  alert?: boolean;
  label: string;
  hint?: string;
}

const GateRow: React.FC<GateRowProps> = ({
  testID, done, alert: isAlert, label, hint,
}) => (
  <VStack testID={testID} space="xs">
    <HStack space="sm" alignItems="center">
      <Icon
        as={done ? CheckCircle : isAlert ? AlertCircle : Clock}
        size="xs"
        color={done ? '$success600' : isAlert ? '$warning600' : '$textSubtle'}
      />
      <Text
        size="sm"
        color={done ? '$success700' : isAlert ? '$warning700' : '$textSubtle'}
        fontWeight="medium"
      >
        {label}
      </Text>
    </HStack>
    {!!hint && (
      <Text size="xs" color="$textSubtle" ml="$6">
        {hint}
      </Text>
    )}
  </VStack>
);

/**
 * ExamPreflightChecklist
 * ----------------------
 * Shows exam gate status as a compact checklist below EventBookingDetails.
 *
 * Gate logic for non-admitted students: session open/closed details are hidden
 * because a session may have been opened for testing with other students.
 * Non-admitted students just see "not started yet."
 */
const ExamPreflightChecklist: React.FC<ExamPreflightChecklistProps> = ({
  meetingUrl,
  isBookingAdmitted,
  isExamOpen,
  isExamClosed,
  onStartQuiz,
}) => {
  const { t } = useTranslation();
  const canStartQuiz = isBookingAdmitted && isExamOpen && !isExamClosed;

  const sessionLabel = (() => {
    if (!isBookingAdmitted) return t('student.eventTask.preflight.sessionNotStarted');
    if (isExamOpen) return t('student.eventTask.preflight.sessionOpen');
    if (isExamClosed) return t('student.eventTask.preflight.sessionPaused');
    return t('student.eventTask.preflight.sessionWaiting');
  })();

  const sessionHint = (() => {
    if (!isBookingAdmitted) return undefined;
    if (isExamClosed) return t('student.eventTask.preflight.sessionPausedHint');
    if (!isExamOpen) return t('student.eventTask.preflight.sessionHint');
    return undefined;
  })();

  const sessionDone = isBookingAdmitted && isExamOpen && !isExamClosed;
  const sessionAlert = isBookingAdmitted && isExamClosed;

  return (
    <VStack
      testID="exam-preflight-checklist"
      space="sm"
      p="$3"
      bg="$backgroundLight50"
      borderRadius="$md"
      borderLeftWidth="$1"
      borderLeftColor={canStartQuiz ? '$success500' : '$warning500'}
    >
      <Text size="xs" color="$textSubtle" fontWeight="semibold">
        {t('student.eventTask.preflight.title')}
      </Text>

      <GateRow
        testID="preflight-gate-admission"
        done={isBookingAdmitted}
        label={isBookingAdmitted
          ? t('student.eventTask.preflight.admissionDone')
          : t('student.eventTask.preflight.admissionWaiting')}
        hint={!isBookingAdmitted ? t('student.eventTask.preflight.admissionHint') : undefined}
      />

      {!!meetingUrl && (
        <Button
          testID="preflight-join-call-button"
          size="sm"
          action="primary"
          variant={canStartQuiz ? 'outline' : 'solid'}
          ml="$6"
          alignSelf="flex-start"
          onPress={() => {
            // Linking.openURL — not window.open (undefined on RN; FLARE-39 / FLARE-7T).
            if (meetingUrl) Linking.openURL(meetingUrl).catch(() => {});
          }}
        >
          <ButtonIcon as={Video} size="xs" mr="$1" />
          <ButtonText>{t('student.eventTask.preflight.joinCall')}</ButtonText>
        </Button>
      )}

      <GateRow
        testID="preflight-gate-session"
        done={sessionDone}
        alert={sessionAlert}
        label={sessionLabel}
        hint={sessionHint}
      />

      {canStartQuiz && !!onStartQuiz && (
        <Button
          testID="preflight-start-quiz-button"
          size="lg"
          action="primary"
          mt="$2"
          onPress={onStartQuiz}
        >
          <ButtonIcon as={Play} size="sm" mr="$2" />
          <ButtonText fontWeight="bold">{t('student.eventTask.preflight.startQuiz')}</ButtonText>
        </Button>
      )}
    </VStack>
  );
};

export default ExamPreflightChecklist;
